// Analytics dashboard (slice 34, ADR 0031).
//
// Reads analytics_rollups_daily via GET /v1/organizations/:orgId/
// outlets/:outletId/analytics?days=N, renders three totals tiles,
// a per-event-type table, and a window picker (7 / 30 / 90 days).
//
// No charts in v1 — just totals + a small Unicode-block sparkline on
// each table row. The sparkline is cheap enough to render inline;
// charts become their own slice when the dashboard needs more.

import { useMemo, useState, type JSX } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiFetch, ApiError } from '../api/client.js';
import type { AnalyticsResponse } from '../api/types.js';
import { useAuth } from '../auth/AuthContext.js';

const WINDOWS = [7, 30, 90] as const;
type WindowDays = (typeof WINDOWS)[number];

export function AnalyticsPage(): JSX.Element {
  const { orgId, outletId } = useParams<{ orgId: string; outletId: string }>();
  const { user } = useAuth();
  const [days, setDays] = useState<WindowDays>(30);

  const analyticsKey = ['analytics', orgId, outletId, days] as const;
  const analytics = useQuery({
    queryKey: analyticsKey,
    queryFn: () =>
      apiFetch<AnalyticsResponse>(
        `/organizations/${orgId}/outlets/${outletId}/analytics?days=${days}`,
      ),
    enabled: !!orgId && !!outletId,
  });

  const byEventType = useMemo(() => groupByEventType(analytics.data), [analytics.data]);
  const todayCount = useMemo(() => countEventsOnToday(analytics.data), [analytics.data]);

  const errorMessage = mutationErrorMessage(analytics.error);

  return (
    <main className="page">
      <header className="page-header">
        <div>
          <h1>Analytics</h1>
          <p className="muted">
            Signed in as <strong>{user?.email}</strong> ·{' '}
            <Link to={`/orgs/${orgId}/outlets/${outletId}/menus`}>Back to menus</Link>
          </p>
        </div>
      </header>

      <section className="analytics-window">
        <h2>Window</h2>
        <div className="chip-row" role="radiogroup" aria-label="Window">
          {WINDOWS.map((n) => (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={days === n}
              className={`chip ${days === n ? 'chip-active' : ''}`}
              onClick={() => setDays(n)}
            >
              Last {n} days
            </button>
          ))}
        </div>
      </section>

      {analytics.isLoading ? <p>Loading…</p> : null}
      {errorMessage ? (
        <p className="error" role="alert">
          {errorMessage}
        </p>
      ) : null}

      {analytics.data ? (
        <>
          <section className="tiles">
            <div className="tile">
              <div className="tile-label">Total events</div>
              <div className="tile-value">{formatNumber(analytics.data.totals.eventCount)}</div>
            </div>
            <div className="tile">
              <div className="tile-label">Unique sessions</div>
              <div className="tile-value">{formatNumber(analytics.data.totals.uniqueSessions)}</div>
            </div>
            <div className="tile">
              <div className="tile-label">Events today</div>
              <div className="tile-value">{formatNumber(todayCount)}</div>
            </div>
          </section>

          <section className="analytics-table-wrap">
            <h2>By event type</h2>
            {byEventType.length === 0 ? (
              <p className="muted">
                No events in this window yet. Numbers appear here within ~5 min of the first scan.
              </p>
            ) : (
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th scope="col">Event type</th>
                    <th scope="col">Total count</th>
                    <th scope="col">Unique sessions</th>
                    <th scope="col">Daily trend</th>
                  </tr>
                </thead>
                <tbody>
                  {byEventType.map((row) => (
                    <tr key={row.eventType}>
                      <th scope="row">
                        <code>{row.eventType}</code>
                      </th>
                      <td>{formatNumber(row.eventCount)}</td>
                      <td>{formatNumber(row.uniqueSessions)}</td>
                      <td>
                        <span
                          className="sparkline"
                          aria-label={`${days}-day trend for ${row.eventType}`}
                        >
                          {sparkline(row.daily)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      ) : null}
    </main>
  );
}

interface AggregatedRow {
  readonly eventType: string;
  readonly eventCount: number;
  readonly uniqueSessions: number;
  readonly daily: readonly number[];
}

export function groupByEventType(data: AnalyticsResponse | undefined): readonly AggregatedRow[] {
  if (!data) return [];
  const map = new Map<
    string,
    { eventCount: number; uniqueSessions: number; byDay: Map<string, number> }
  >();
  for (const row of data.series) {
    const existing = map.get(row.eventType) ?? {
      eventCount: 0,
      uniqueSessions: 0,
      byDay: new Map<string, number>(),
    };
    existing.eventCount += row.eventCount;
    existing.uniqueSessions += row.uniqueSessions;
    existing.byDay.set(row.bucketStart.slice(0, 10), row.eventCount);
    map.set(row.eventType, existing);
  }
  // Produce dense daily arrays — one bucket per day in the window —
  // so the sparkline shows gaps as zero instead of as missing data.
  const from = new Date(data.from);
  const to = new Date(data.to);
  const dayKeys: string[] = [];
  const cursor = new Date(from.getTime());
  while (cursor < to) {
    dayKeys.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }

  const rows: AggregatedRow[] = [];
  for (const [eventType, agg] of map.entries()) {
    const daily = dayKeys.map((k) => agg.byDay.get(k) ?? 0);
    rows.push({
      eventType,
      eventCount: agg.eventCount,
      uniqueSessions: agg.uniqueSessions,
      daily,
    });
  }
  // Alphabetical by event type — stable + a reasonable default.
  rows.sort((a, b) => a.eventType.localeCompare(b.eventType));
  return rows;
}

export function countEventsOnToday(data: AnalyticsResponse | undefined): number {
  if (!data) return 0;
  const today = new Date().toISOString().slice(0, 10);
  let n = 0;
  for (const row of data.series) {
    if (row.bucketStart.slice(0, 10) === today) n += row.eventCount;
  }
  return n;
}

const SPARK_CHARS = ['▁', '▂', '▃', '▄', '▅', '▆', '▇', '█'] as const;

export function sparkline(values: readonly number[]): string {
  if (values.length === 0) return '';
  const max = Math.max(...values);
  if (max === 0) {
    return SPARK_CHARS[0]!.repeat(values.length);
  }
  return values
    .map((v) => {
      const idx = Math.min(
        SPARK_CHARS.length - 1,
        Math.round((v / max) * (SPARK_CHARS.length - 1)),
      );
      return SPARK_CHARS[idx]!;
    })
    .join('');
}

function formatNumber(n: number): string {
  return new Intl.NumberFormat('en-IN').format(n);
}

function mutationErrorMessage(err: unknown): string | null {
  if (!err) return null;
  if (err instanceof ApiError) {
    if (err.status === 403) return 'You do not have permission to view analytics.';
    if (err.status === 404) return 'Analytics data not found for this outlet.';
    if (err.status === 0) return 'Network error — check your connection.';
    const body = err.body as { message?: { code?: string }; code?: string } | undefined;
    const code = body?.message?.code ?? body?.code;
    if (typeof code === 'string') return `Request failed: ${code}`;
    return 'Something went wrong.';
  }
  return 'Something went wrong.';
}
